/**
 * Reads OPBridge's lighting preset catalogue into `lighting/presets.ts`.
 *
 * On demand, not polled. The catalogue is OPBridge's built-in list and
 * changes only on an OPBridge update, and nothing about a preset's RUNNING
 * state is readable anyway (see `presets.ts`), so there is nothing a poll
 * would keep honest. A button or the Inspector asks when it first needs the
 * list; a failed read is retried at most every `RETRY_MS`, and only once the
 * bridge is connected, so a machine that was offline recovers on its own.
 */

import { getConnection } from '../opbridge/store.ts';
import { OpbridgeError } from '../opbridge/httpClient.ts';
import { presetState, recordPresets, recordPresetsFailure } from './presets.ts';

const RETRY_MS = 10_000;
const inFlight = new Set<string>();

/** Read one bridge's catalogue now and record the outcome. Never throws.
 *  `bridgeRowId` is a LOCAL ROW id (see `bridgeStateKey`). */
export async function refreshPresets(bridgeRowId: string): Promise<void> {
  if (!bridgeRowId || inFlight.has(bridgeRowId)) return;
  const conn = getConnection(bridgeRowId);
  if (!conn) { recordPresetsFailure(bridgeRowId, 'error', 'No such bridge.'); return; }
  if (conn.status() !== 'connected') { recordPresetsFailure(bridgeRowId, 'error', `Bridge is ${conn.status()}.`); return; }
  inFlight.add(bridgeRowId);
  try {
    const res = await conn.httpClient().lightingPresets();
    recordPresets(bridgeRowId, Array.isArray(res?.presets) ? res.presets : []);
  } catch (err) {
    const status = err instanceof OpbridgeError ? err.httpStatus : undefined;
    const msg = err instanceof Error ? err.message : String(err);
    if (status === 401 || status === 403) {
      recordPresetsFailure(bridgeRowId, 'unauthorized',
        'OPBridge has not given this device lighting presets. Re-pair it, or update OPBridge.');
    } else if (status === 404) {
      recordPresetsFailure(bridgeRowId, 'unsupported',
        'This OPBridge is too old for lighting presets (needs v1.15.154 or later).');
    } else {
      recordPresetsFailure(bridgeRowId, 'error', msg);
    }
  } finally {
    inFlight.delete(bridgeRowId);
  }
}

/** Read if never read, or if the last read failed more than `RETRY_MS` ago
 *  and the bridge is connected now. Cheap to call from an effect. */
export function ensurePresets(bridgeRowId: string): void {
  if (!bridgeRowId) return;
  const st = presetState(bridgeRowId);
  if (st.status === 'ok') return;
  if (st.status !== 'unknown') {
    if (Date.now() - st.at < RETRY_MS) return;
    if (getConnection(bridgeRowId)?.status() !== 'connected') return;
  }
  void refreshPresets(bridgeRowId);
}
